import FuseLayout from "@fuse/core/FuseLayout";
import { SnackbarProvider } from "notistack";
import themeLayouts from "@/components/theme-layouts/themeLayouts";
import FuseSettingsProvider from "@fuse/core/FuseSettings/FuseSettingsProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS } from "date-fns/locale/en-US";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import ErrorBoundary from "@fuse/utils/ErrorBoundary";
import Authentication from "@auth/Authentication";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import MainThemeProvider from "../contexts/MainThemeProvider";
import { AppContextProvider } from "@/contexts/appContext/AppContextProvider";
import { NavbarContextProvider } from "@/components/theme-layouts/components/navbar/contexts/NavbarContext/NavbarContextProvider";
import { QuickPanelProvider } from "@/components/theme-layouts/components/quickPanel/contexts/QuickPanelContext/QuickPanelContextProvider";
import RootThemeProvider from "@/contexts/RootThemeProvider";
import { NavigationContextProvider } from "@/components/theme-layouts/components/navigation/contexts/NavigationContextProvider";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { FooterTabsProvider } from "@/contexts/tabsContext/HeaderTabsContext";
import { LocalizationProviderWrapper } from "@/contexts/LocalizationProvider";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import QueryCachePersistManager from "@/utils/tanstack/QueryCachePersistManager";
import { queryClient } from "@/utils/tanstack/queryClient";
import { WebSocketProvider } from "@/contexts/websocket";
import { SnackbarCloseAction } from "@/utils/notistack/snackbarActions";
import AppUpdateWatcher from "@/components/theme-layouts/components/appUpdateWatcher/AppUpdateWatcher";
import { AppAssistenteIAProvider } from "@/components/avulsos/appAssistenteIA";

const SnackbarIcon = ({ icon }: { icon: string }) => (
	<span className="app-snackbar-icon" aria-hidden="true">
		<FuseSvgIcon size={22} color="inherit">
			{icon}
		</FuseSvgIcon>
	</span>
);

const snackbarIconVariants = {
	default: <SnackbarIcon icon="lucide:bell" />,
	success: <SnackbarIcon icon="lucide:circle-check" />,
	error: <SnackbarIcon icon="lucide:circle-x" />,
	warning: <SnackbarIcon icon="lucide:triangle-alert" />,
	info: <SnackbarIcon icon="lucide:info" />,
};

function App() {
	return (
		<ErrorBoundary>
			<AppContextProvider>
				<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
					<QueryClientProvider client={queryClient}>
						<Authentication>
							<QueryCachePersistManager queryClient={queryClient} />
							<LocalizationProviderWrapper>
								<CompanyProvider>
									<FuseSettingsProvider>
										{/* Theme Provider */}
										<RootThemeProvider>
											<MainThemeProvider>
												<NavbarContextProvider>
													<NavigationContextProvider>
														<FooterTabsProvider>
															{/* Notistack Notification Provider */}
															<SnackbarProvider
																maxSnack={2}
																anchorOrigin={{
																	vertical: "bottom",
																	horizontal: "right",
																}}
																autoHideDuration={6000}
																action={SnackbarCloseAction}
																iconVariant={snackbarIconVariants}
																classes={{
																	containerRoot:
																		"bottom-0 right-0 mb-13 md-17 mr-2 lg:mr-20 z-99",
																}}
															>
																<AppUpdateWatcher />
																<WebSocketProvider>
																	<AppContent />
																</WebSocketProvider>
															</SnackbarProvider>
														</FooterTabsProvider>
													</NavigationContextProvider>
												</NavbarContextProvider>
											</MainThemeProvider>
										</RootThemeProvider>
									</FuseSettingsProvider>
								</CompanyProvider>
							</LocalizationProviderWrapper>
						</Authentication>
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				</LocalizationProvider>
			</AppContextProvider>
		</ErrorBoundary>
	);
}

function AppContent() {
	return (
		<QuickPanelProvider>
			<AppAssistenteIAProvider>
				<FuseLayout layouts={themeLayouts} />
			</AppAssistenteIAProvider>
		</QuickPanelProvider>
	);
}

export default App;
